import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Dialog, DialogContent, Typography, IconButton, Grid, InputAdornment, Popover, Chip, FormHelperText } from '@material-ui/core'
import Button from 'UI/Button/ButtonWithPreloader'
import { ReactComponent as SplitIcon } from 'assets/svg/SplitIcon.svg'
import { ReactComponent as CloseIcon } from 'assets/svg/CloseIcon.svg'
import { useQuery } from 'react-query'
import { getRepLight } from 'services/rep'
import { IRepListItemLight } from 'typescript/interfaces/rep'
import RepAutoComplete from 'components/RepAutoComplete'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import SplitCommissionForm from 'components/InhouseDealForm/SplitCommissionForm'
import { calcProportionInPercents } from 'utils/rounding'
import { getInitialValuesForCommission } from 'utils/commissionUtils'
import { useSnackbar } from 'notistack'
import { 
  COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, 
  COMMISSION_DATE_EARLIER, 
  COMMISSION_DUE_DATE_IN_PAST, 
  INVALID_DATE_MESSAGE } from 'constants/errors'
import { COMMISSION_WAS_PAID_WARNING } from 'constants/warnings'
import { useDealInformationFormStyles } from 'components/InhouseDealForm/styles'
import { DECIMAL_PLACES } from 'constants/calcConstants'
import { isBeforeDate, isDateInPast, isSameCalendarDate } from 'utils/dateUtils'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import convertDateForPicker from 'utils/convertDateForPicker'
import { isValid } from 'date-fns/esm'
import DatePicker from 'UI/DatePicker/DatePicker'
import { useISORepSelectorStyles } from './styles'

interface RepSelectorProps {
  value?: {
    ids?: number[]
    fee?: number
    splitCommission?: any[]
    commissionDueDate?: string
  }
  onChange: (value: any) => void
  disabled?: boolean
  advanceAmount?: number
  dealStartDate?: string
  hideCommissionDueDateField: boolean
  disableCommissionDueDateField: boolean
}

const RepSelector: React.FC<RepSelectorProps> = (
  { value, onChange, disabled = false, advanceAmount = 0, dealStartDate, hideCommissionDueDateField, disableCommissionDueDateField }) => {
  const s = useDealInformationFormStyles()
  const classes = useISORepSelectorStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [splitPopoverAnchor, setSplitPopoverAnchor] = useState<HTMLElement | null>(null)
   const [dateError, setDateError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    ids: value?.ids || [],
    fee: value?.fee?.toString() || '',
    splitCommission: value?.splitCommission || [],
    commissionDueDate: value?.commissionDueDate || undefined
  })

  // Fetch data for autocomplete
  const { data: repData } = useQuery(['rep'], () => getRepLight({ PageSize: 4000 }))

  const selectedItems = useMemo(() => {
    if (repData) {
      return repData.data.filter((item: IRepListItemLight) => formData.ids.includes(item.id))
    }
    return []
  }, [formData.ids, repData])

  // Update formData when value prop changes (e.g., when deal data is loaded)
  // Only reset form data if dialog is not open to prevent losing user progress
  useEffect(() => {
    if (!isDialogOpen) {
      setFormData({
        ids: value?.ids || [],
        fee: value?.fee?.toString() || '',
        splitCommission: value?.splitCommission || [],
        commissionDueDate: value?.commissionDueDate
      })
    }
  }, [value, isDialogOpen])

  const handleOpenDialog = () => {
    if (value) {
      setFormData({
        ids: value.ids || [],
        fee: value.fee?.toString() || '',
        splitCommission: value.splitCommission || [],
        commissionDueDate: value?.commissionDueDate
      })
      setDateError(null);
    }
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  const handleSave = () => {
    const feeAsNumber = parseFloat(formData.fee) || 0

    // Allow 0 fee when no Reps are selected or when user explicitly sets it to 0
    if (feeAsNumber < 0) {
      enqueueSnackbar(`Please enter a valid Rep fee`, { variant: 'error' })
      return
    }

    // Check if commission exceeds 15% of advance amount
    if (formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber) {
      enqueueSnackbar(COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT, { variant: 'error' })
      return
    }

    const dataToSave = {
      ...formData,
      fee: feeAsNumber, // Save as number
      commissionDueDate: feeAsNumber ? formData.commissionDueDate : undefined
    }

    onChange(dataToSave)
    setIsDialogOpen(false)
  }

  const handleIdsChange = (newIds: number[]) => {
    setFormData(prev => {
      const prevIds = prev.ids || []
      const prevIdsSorted = [...prevIds].sort()
      const newIdsSorted = [...newIds].sort()
      const isSameSelection =
        prevIdsSorted.length === newIdsSorted.length && prevIdsSorted.every((id, index) => id === newIdsSorted[index])

      return {
        ...prev,
        ids: newIds,
        splitCommission: isSameSelection && newIds.length > 1 ? prev.splitCommission : [],
      }
    })
  }

  const handleFeeChange = (newFee: string) => {
    setFormData(prev => ({
      ...prev,
      fee: newFee,
      splitCommission: [],
    }))
  }

  const handleSplitCommissionChange = (newSplitCommission: any[]) => {
    setFormData(prev => ({
      ...prev,
      splitCommission: newSplitCommission,
    }))
  }

  const renderSplitCommissionForm = () => {
    // Get all Reps that should be in the split (including existing ones)
    // Order: House Rep first, then other Reps (same as PSF fee split)
    const allReps = []

    // Always add House Rep (ID: 1) first - default for create and existing for edit
    const houseRepFromData = repData?.data.find((item: IRepListItemLight) => item.id === 1)
    allReps.push({
      id: 1,
      name: houseRepFromData?.name || 'House Rep',
    })

    // Then add all other Reps (excluding House Rep)
    const otherRepIds = [...selectedItems.map(item => item.id), ...(formData.splitCommission?.map(item => item.id).filter(id => id !== 1) || [])]

    // Remove duplicates while preserving order
    const uniqueOtherRepIds = otherRepIds.filter((id, index) => otherRepIds.indexOf(id) === index)

    uniqueOtherRepIds.forEach(id => {
      // First try to find in selected items
      const selectedItem = selectedItems.find(item => item.id === id)
      if (selectedItem) {
        allReps.push({
          id,
          name: (selectedItem as IRepListItemLight).name,
        })
      } else {
        // If not in selected items, find in repData (all available Reps)
        const repFromData = repData?.data.find((item: IRepListItemLight) => item.id === id)
        if (repFromData) {
          allReps.push({
            id,
            name: repFromData.name,
          })
        }
      }
    })

    const feeAmount = parseFloat(formData.fee) || 0
    const initialValues = getInitialValuesForCommission(allReps, formData.splitCommission, feeAmount)

    return (
      <div className={s.popoverContainer}>
        <SplitCommissionForm
          key={`rep-split-${JSON.stringify(initialValues)}-${selectedItems.length}`}
          handleClosePopover={() => setSplitPopoverAnchor(null)}
          propsSetFieldValue={(name: string, fieldValue: any) => {
            if (name === 'repSplitCommission') {
              handleSplitCommissionChange(fieldValue)
            }
          }}
          propsFormValues={{ repSplitCommission: initialValues }}
          initialValues={initialValues}
          selectedReps={allReps}
          disabled={disabled}
          classes={{ root: s.popoverTextField }}
          name="repSplitCommission"
          totalFeeAmount={feeAmount}
        />
      </div>
    )
  }

   const isPastDateAllowed = useCallback(
    (date: Date) => isSameCalendarDate(date, value?.commissionDueDate),
    [value?.commissionDueDate]
  )

  const handleCommissionDateChange = useCallback((date: MaterialUiPickersDate) => {
    const converted = convertDateForPicker(date) as Date | null
    let commissionDueDate: string | undefined;
    let error: string | null = null;

    if (converted) {
      if (isValid(converted)) {
        commissionDueDate = converted.toISOString()
        
        if (dealStartDate && isBeforeDate(converted, dealStartDate)) {
          error = COMMISSION_DATE_EARLIER
        } else if (isDateInPast(converted) && !isPastDateAllowed(converted)) {
          error = COMMISSION_DUE_DATE_IN_PAST
        }
      } else {
        error = INVALID_DATE_MESSAGE
      }
    }

    setDateError(error);
    setFormData(prev => ({
      ...prev,
      commissionDueDate: commissionDueDate,
    }))

  }, [dealStartDate, isPastDateAllowed])

  return (
    <>
      <Box>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          disabled={disabled}
          className={formData.ids.length > 0 ? classes.addButtonSelected : classes.addButton}
          fullWidth
        >
          Add Rep
        </Button>
      </Box>

      {/* Main Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} className={classes.dialog}>
        <DialogContent className={classes.dialogContent}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" className={classes.dialogHeader}>
            <Typography variant="h4">Add Rep</Typography>
            <IconButton aria-label="close" onClick={handleCloseDialog} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Comission due date section */}
          {!hideCommissionDueDateField && (<div className={classes.section}>
            <DatePicker
                disabled={disabled || disableCommissionDueDateField}
                name="commissionDate"
                value={formData.commissionDueDate ? formData.commissionDueDate : null}
                shouldDisableDate={((date) => {
                  if (!date) return false;
                  if (isDateInPast(date) && !isPastDateAllowed(date)) return true;
                  if (!dealStartDate) return false;
                  return isBeforeDate(date, dealStartDate);
                })}
                onChange={handleCommissionDateChange}
                error={Boolean(dateError)}
                helperText={dateError}
                inputVariant="outlined"
                variant="dialog"
                type="keyboard"
                fullWidth
                label="Commission Due Date"
              />
              {disableCommissionDueDateField && (
                <FormHelperText error>{COMMISSION_WAS_PAID_WARNING}</FormHelperText>
              )}
          </div>
          )}

          {/* Selection Section */}
          <div className={classes.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RepAutoComplete
                  multiple
                  disabled={disabled}
                  isHouseHidden
                  label="Rep"
                  repId={formData.ids}
                  onChange={(_e, selectedValue: IRepListItemLight[]) => {
                    const ids = selectedValue.map(v => v.id)
                    handleIdsChange(ids)
                  }}
                />
              </Grid>
              {selectedItems.length > 0 && (
                <Grid item xs={12}>
                  <div className={classes.selectedItems}>
                    <Typography variant="caption" color="textSecondary">
                      Selected Reps:
                    </Typography>
                    <Box mt={1}>
                      {selectedItems.map((item: any) => (
                        <Chip key={item.id} label={item.name} className={classes.chip} size="small" />
                      ))}
                    </Box>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>

          {/* Fee Section */}
          <div className={classes.section}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DecimalTextField
                  variant="outlined"
                  fullWidth
                  label="Rep Fee"
                  value={formData.fee}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFeeChange(e.target.value)}
                  disabled={disabled}
                  decimalScale={DECIMAL_PLACES}
                  helperText={(() => {
                    const feeAsNumber = parseFloat(formData.fee) || 0
                    return formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber
                      ? COMISSION_NEED_TO_BE_LOWER_THAN_15_PERCENT
                      : feeAsNumber > 0
                      ? `${calcProportionInPercents(advanceAmount, feeAsNumber)}% of advance amount`
                      : 'Enter fee amount'
                  })()}
                  error={(() => {
                    const feeAsNumber = parseFloat(formData.fee) || 0
                    return formData.ids.length > 0 && advanceAmount * 0.15 < feeAsNumber
                  })()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={!(parseFloat(formData.fee) || 0) || !formData.ids.length || disabled}
                          aria-label="Split commission to Reps"
                          onClick={e => setSplitPopoverAnchor(e.currentTarget)}
                        >
                          <SplitIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </div>
          <div className={classes.buttonContainer}>
            <Button onClick={handleCloseDialog} color="secondary" variant="contained" className={classes.cancelButton}>
              Cancel
            </Button>
            
            <Button onClick={handleSave} 
              color="primary" 
              variant="contained" 
              type="submit" 
              className={classes.saveButton}
              disabled={Boolean(dateError)}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Split Commission Popover */}
      <Popover
        open={Boolean(splitPopoverAnchor)}
        anchorEl={splitPopoverAnchor}
        onClose={() => setSplitPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={classes.popoverContainer}>{renderSplitCommissionForm()}</div>
      </Popover>
    </>
  )
}

export default RepSelector
